import {Permission, Role} from './enums&types.js';

// the role -> permission table that used to be the createRole calls in initStaticObjects.ts
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
    [Role.admin]           : [Permission.admin, Permission.dashboard],
    [Role.reporter]        : [Permission.report, Permission.dashboard],
    [Role.super_reporter]  : [Permission.api],
    [Role.acceptant]       : [Permission.accept],
    [Role.exporter]        : [Permission.export],
    [Role.reporter_manager]: [Permission.manage_reporters],
    [Role.dashboard_viewer]: [Permission.dashboard],
};

export function permissiesVan(roles: Role[]): Permission[] {
    return [...new Set(roles.flatMap(r => ROLE_PERMISSIONS[r] ?? []))];
}

export const SESSIE_IDLE_DAGEN: number           = 14;
export const SESSIE_MAX_DAGEN: number            = 90;
export const RESET_TOKEN_UREN: number            = 1;
export const ONBOARDING_TOKEN_DAGEN: number      = 7;
export const LOGIN_CODE_MINUTEN: number          = 10;
export const LOGIN_CODE_MAX_POGINGEN: number     = 5;
export const VERTROUWDE_BROWSER_DAGEN: number    = 30;
export const WACHTWOORD_MIN_LENGTE: number       = 8;
export const THROTTLE_MAX_POGINGEN: number       = 5;
export const THROTTLE_VENSTER_MINUTEN: number    = 5;
export const THROTTLE_BLOKKADE_SECONDEN: number  = 30;
export const SESSIE_COOKIE: string               = 'zzc_sessie';
export const BROWSER_COOKIE: string              = 'zzc_browser';

export type TokenDoel = 'reset' | 'onboarding' | 'login';
export type MailSoort = 'welkom' | 'reset' | 'code';

// what the AuthLog records: the three mails, and what happens with a reset or onboarding link afterwards
export type AuthLogSoort = MailSoort | 'link-geopend' | 'link-ongeldig' | 'wachtwoord-gezet';

export const AUTH_LOG_LABELS: Record<AuthLogSoort, string> = {
    'welkom'          : 'welkomstmail',
    'reset'           : 'wachtwoordmail',
    'code'            : 'inlogcode',
    'link-geopend'    : 'link geopend',
    'link-ongeldig'   : 'ongeldige link',
    'wachtwoord-gezet': 'wachtwoord gezet',
};

// one AuthLog node; answer of GET /api/admin/auth-log is a list of these, newest first.
// A mail's tekst (with its link or code) is only kept where no mail is sent, so a production log never holds a live
// secret; an invalid link is logged with the ip only, never with the token that was tried
export type AuthLogRegel = {
    id: string;
    tijd: string; // ISO
    soort: AuthLogSoort;
    aan?: string;    // the user's e-mail; absent for an invalid link
    ip?: string;     // the link kinds
    gelukt: boolean; // mail sent, link valid, password set
    fout?: string;   // the SMTP error when sending failed
    tekst?: string;  // the mail text, only when MAIL_VERSTUREN is off
};

// one live session of a user; answer of POST /api/obj/user/sessies. id is the stored hash, never the secret
export type SessieInfo = {
    id: string;
    aangemaaktOp: string; // ISO
    laatstGezienOp: string;
    ip: string;
    browser: string;
    huidig: boolean; // the session the caller is using now
};

// live sessions and trusted browsers of one user; answer of POST /api/obj/user/sessie-overzicht is one per e-mail
export type SessieTelling = {
    sessies: number;
    browsers: number;
};

// answer of POST /api/auth/start: log in with a password, or a set-password mail has just been sent
export type StartAntwoord = {
    status: 'wachtwoord' | 'mail' | 'geblokkeerd';
};

// answer of POST /api/auth/login and /api/auth/login-code
export type LoginAntwoord = {
    status: 'ok' | 'code' | 'fout' | 'geblokkeerd';
    pogingenOver?: number; // login-code: attempts left after a wrong code
};

// answer of POST /api/auth/wachtwoord-zetten
export type WachtwoordAntwoord = {
    status: string;
    message?: string;
};
